<template>
  <div class="promotion-products-table">
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div class="text-sm text-gray-600">
        {{ $t("promotions.products.found", { count: total }) }}
      </div>

      <OurSelect
        v-model="perPage"
        :label="$t('label.show')"
        class="w-40"
        :options="perPageOptions"
        item-label="label"
        value-key="value"
        :allow-empty="false"
        size="sm"
      />
    </div>

    <div class="relative">
      <OurTable
        :headers="tableHeaders"
        :items="paginatedItems"
        :selectable="!readonly"
        :show-selection-header="!readonly"
        :table-fixed="false"
        selection-key="id"
        :selected-ids="selectedIds"
        @update:selectedIds="onUpdateSelectedIds"
      >
        <template v-if="!readonly" #thead-actions>
          <OurButton
            size="sm"
            variant="thirdary"
            filled
            :text="$t('promotions.products.bulkDelete')"
            :disabled="!selectedIds.length"
            @click="onBulkDelete"
          />
        </template>

        <template v-if="!readonly" #cell-delete="{ value }">
          <OurButton
            size="sm"
            variant="thirdary"
            filled
            :text="$t('button.remove')"
            @click="onDeleteProduct(value)"
          />
        </template>
      </OurTable>

      <div v-if="loading" class="loading-overlay">
        <div class="loading-content">
          <div class="loader" />
          <div v-if="loadingText" class="loading-text">
            {{ loadingText }}
          </div>
        </div>
      </div>
    </div>

    <OurPagination
      v-if="isMoreThanOnePage"
      class="mt-4"
      :current-page="displayCurrentPage"
      :per-page="displayPerPage"
      :total="total"
      :disabled="loading"
      @pageChange="onPageChange"
    />

    <div
      v-if="notFoundSkus.length"
      class="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4"
    >
      <div class="mb-2 font-medium text-orange-800">
        {{ $t("promotions.products.notFound") }}
      </div>
      <div class="text-sm text-orange-700">
        {{ notFoundSkus.join(", ") }}
      </div>
    </div>

    <div v-if="duplicateSkus.length" class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div class="mb-2 font-medium text-gray-800">
        {{ $t("promotions.products.duplicateArticles") }}
      </div>
      <div class="text-sm text-gray-700">
        {{ duplicateSkus.join(", ") }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PromotionProductsTable",

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: null,
    },

    notFoundSkus: {
      type: Array,
      default: () => [],
    },

    duplicateSkus: {
      type: Array,
      default: () => [],
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    loadingText: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      currentPage: 1,
      perPage: 50,
      selectedIds: [],
      perPageOptions: [
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
      ],
    };
  },

  computed: {
    isServerPagination() {
      return Boolean(this.paginationData);
    },

    total() {
      if (this.isServerPagination) {
        return this.paginationData.total;
      }

      return this.items.length;
    },

    displayCurrentPage() {
      if (this.isServerPagination) {
        return this.paginationData.currentPage;
      }

      return this.currentPage;
    },

    displayPerPage() {
      if (this.isServerPagination) {
        return this.paginationData.perPage;
      }

      return this.perPage;
    },

    tableHeaders() {
      const headers = [
        {
          value: "productCode",
          text: this.$t("promotions.products.productCode"),
          thClass: "w-[10rem]",
          tdClass: "max-w-[12rem] min-w-[8rem]",
        },
        {
          value: "name",
          text: this.$t("promotions.products.productName"),
          thClass: "w-[20rem]",
          tdClass: "max-w-[24rem] min-w-[12rem]",
        },
        {
          value: "categoryName",
          text: this.$t("promotions.products.category"),
          thClass: "w-[12rem]",
          tdClass: "max-w-[14rem] min-w-[10rem]",
        },
      ];

      if (!this.readonly) {
        headers.push({
          value: "delete",
          text: "",
          thClass: "w-[8rem]",
          tdClass: "max-w-[8rem] min-w-[8rem]",
        });
      }

      return headers;
    },

    paginatedItems() {
      const items = this.isServerPagination
        ? this.items
        : this.items.slice((this.currentPage - 1) * this.perPage, this.currentPage * this.perPage);

      return items.map((item) => ({
        ...item,
        categoryName: item.categoryName || "–",
        delete: item.id,
      }));
    },

    isMoreThanOnePage() {
      if (this.isServerPagination) {
        return this.paginationData.lastPage > 1;
      }

      return Math.ceil(this.total / this.perPage) > 1;
    },
  },

  watch: {
    paginationData: {
      handler(data) {
        if (data?.perPage && data.perPage !== this.perPage) {
          this.perPage = data.perPage;
        }
      },
      immediate: true,
    },

    items() {
      if (!this.isServerPagination) {
        const itemIds = new Set(this.items.map((item) => item.id));

        this.selectedIds = this.selectedIds.filter((id) => itemIds.has(id));
        this.currentPage = 1;
      }
    },

    perPage(perPage) {
      if (this.isServerPagination) {
        this.$emit("pageChange", { page: 1, perPage });

        return;
      }

      this.currentPage = 1;
    },
  },

  methods: {
    onUpdateSelectedIds(selectedIds) {
      this.selectedIds = [...new Set(selectedIds)];
    },

    onDeleteProduct(id) {
      this.selectedIds = this.selectedIds.filter((selectedId) => selectedId !== id);
      this.$emit("delete", [id]);
    },

    onBulkDelete() {
      this.$emit("delete", this.selectedIds);
      this.selectedIds = [];
    },

    onPageChange(page) {
      if (this.isServerPagination) {
        this.$emit("pageChange", { page, perPage: this.displayPerPage });
      } else {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.loading-text {
  font-size: 0.875rem;
  color: #4b5563;
}

.loader {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
